import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";

const POSITIONS = [
  { value: "Shooter", label: "Shooter" },
  { value: "Big", label: "Big" },
  { value: "Ball Handler", label: "Ball Handler" },
  { value: "Cutter", label: "Cutter" },
  { value: "Plays D", label: "Plays D" },
  { value: "Utility", label: "Utility" },
];

const defaultForm = { name: "", number: "", position: "Utility", email: "", phone: "" };

const PlayerModal = ({ isOpen, mode, player, onClose, onSave }) => {
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && player) {
        setForm({
          name: player?.name || "",
          number: String(player?.number || ""),
          position: player?.position || "Utility",
          email: player?.email || "",
          phone: player?.phone || "",
        });
      } else {
        setForm(defaultForm);
      }
      setErrors({});
    }
  }, [isOpen, mode, player]);

  if (!isOpen) return null;

  const validate = () => {
    const errs = {};
    if (!form?.name?.trim()) errs.name = "Player name is required";
    else if (form?.name?.trim()?.length < 2) errs.name = "Name must be at least 2 characters";
    if (!form?.number) errs.number = "Jersey number is required";
    else if (isNaN(form?.number) || Number(form?.number) < 0 || Number(form?.number) > 99) errs.number = "Enter a valid jersey number (0-99)";
    if (form?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) errs.email = "Enter a valid email address";
    return errs;
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e?.target?.value }));
    if (errors?.[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }
    onSave({ ...form, number: Number(form?.number) });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.55)" }} onClick={(e) => e?.target === e?.currentTarget && onClose()}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl" style={{ backgroundColor: "var(--color-card)" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(232,93,4,0.12)" }}>
              <Icon name={mode === "edit" ? "Pencil" : "UserPlus"} size={16} color="var(--color-primary)" />
            </div>
            <h2 className="font-heading font-semibold text-lg" style={{ color: "var(--color-foreground)" }}>
              {mode === "edit" ? "Edit Player" : "Add New Player"}
            </h2>
          </div>
          <button className="p-2 rounded-lg transition-athletic hover:bg-gray-100 dark:hover:bg-gray-800" onClick={onClose} aria-label="Close modal">
            <Icon name="X" size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <Input
            label="Full Name"
            type="text"
            placeholder="e.g. Marcus Johnson"
            value={form?.name}
            onChange={handleChange("name")}
            error={errors?.name}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Jersey Number"
              type="number"
              placeholder="0-99"
              value={form?.number}
              onChange={handleChange("number")}
              error={errors?.number}
              min={0}
              max={99}
              required
            />
            <div>
              <label className="block text-sm font-medium font-caption mb-1" style={{ color: "var(--color-foreground)" }}>
                Style <span style={{ color: "var(--color-error)" }}>*</span>
              </label>
              <select
                value={form?.position}
                onChange={handleChange("position")}
                className="w-full px-3 py-2 rounded-lg border text-sm font-caption focus:outline-none"
                style={{ backgroundColor: "var(--color-input)", borderColor: "var(--color-border)", color: "var(--color-foreground)" }}
              >
                {POSITIONS?.map((p) => (
                  <option key={p?.value} value={p?.value}>{p?.label}</option>
                ))}
              </select>
            </div>
          </div>
          <Input
            label="Email (optional)"
            type="email"
            placeholder="player@example.com"
            value={form?.email}
            onChange={handleChange("email")}
            error={errors?.email}
          />
          <Input
            label="Phone (optional)"
            type="tel"
            placeholder="(555) 000-0000"
            value={form?.phone}
            onChange={handleChange("phone")}
          />

          <div className="flex gap-3 pt-2">
            <Button variant="outline" type="button" fullWidth onClick={onClose}>Cancel</Button>
            <Button variant="default" type="submit" fullWidth iconName={mode === "edit" ? "Check" : "UserPlus"} iconPosition="left">
              {mode === "edit" ? "Save Changes" : "Add Player"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlayerModal;